<template>
  <IndicatorCard
    :loading="loading"
    :has-data="data.rows && data.rows.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhuma pendência encontrada')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0">
      <v-col cols="12" md="6">
        <div class="chart-title text-center mb-2 font-weight-medium">
          {{ $t('Pendências por Tipo de Pendência') }}
        </div>
        <BaseEChart :option="pieOption" :loading="loading" height="340px" />
      </v-col>

      <v-col cols="12" md="6">
        <v-simple-table class="main-table" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('Tipo de O.S.') }}</th>
                <th class="text-center">{{ $t('-30 Dias') }}</th>
                <th class="text-center">{{ $t('+30 Dias') }}</th>
                <th class="text-center">{{ $t('+90 Dias') }}</th>
                <th class="text-center">{{ $t('Total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in data.rows"
                :key="index"
                :class="{ 'type-header-row': !row.isSubType, 'subtype-row': row.isSubType }"
              >
                <td>
                  <span class="d-flex align-center">
                    <v-icon v-if="row.isSubType" small class="subtype-icon mr-1">
                      mdi-subdirectory-arrow-right
                    </v-icon>
                    <span :class="{ 'font-weight-bold': !row.isSubType }">
                      {{ $t(row.displayName) }}
                    </span>
                  </span>
                </td>
                <td class="text-center">{{ row.below30 }}</td>
                <td class="text-center">{{ row.above30 }}</td>
                <td class="text-center above90-col">{{ row.above90 }}</td>
                <td class="text-center font-weight-medium">{{ row.total }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="$t('Digite sua análise crítica para o indicador abaixo')"
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildPieOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

const PENDING_STATUS_COLORS = {
  'Aguardando Orçamento': '#FDB578',
  'Aguardando Aprovação de Orçamento': '#ED8990',
  'Aguardando Peça': '#BBCF20',
  'Em Manutenção Interna': '#0F3565',
  'Manutenção Terceirizada In Loco': '#426483',
  'Manutenção Terceirizada Externa': '#212950',
  'Aguardando Avaliação Externa': '#7EE882',
}

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  data: () => ({
    excelLoading: false,
    extensions: [
      History,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak,
    ],
    filter: {
      page: 1,
      pageSize: 20,
      institutionId: null,
      unidadeIds: [],
      startDate: null,
      endDate: null,
      review: '',
    },
    startDate: null,
    endDate: null,
    apiService: new ApiService(),
    loading: true,
    data: {
      rows: [],
      chartsModel: [],
    },
  }),

  computed: {
    indicator() {
      return getIndicatorById('pending_os_by_type') || {}
    },

    pieOption() {
      const items = (this.data.chartsModel || []).filter((x) => x.quantity > 0)
      return buildPieOption({
        data: items.map((x) => ({
          name: this.$t(x.name),
          value: x.quantity,
          color: PENDING_STATUS_COLORS[x.name] || '#aaa',
        })),
        showLegend: true,
      })
    },
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('pending_os_by_type').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          const response = JSON.parse(this.filterProp.data)
          this.data = response.content
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/pendingOsByType',
          this.filter
        )
        this.data = response.content
      } catch {
        // keep previous state
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.type-header-row td {
  background-color: #f0f4f8;
}

.subtype-row td {
  color: #555;
  font-size: 0.9em;
}

.subtype-icon {
  color: #888;
}

.above90-col {
  color: #c62828;
  font-weight: 500;
}

.chart-title {
  font-size: 0.95rem;
  color: #333;
}
</style>
